import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useWallet } from '@/lib/data/wallet';
import { AuthPromptModal, MembershipPromptModal } from '@/components/services/ServiceAccessModals';

interface OutingOption {
  id: string;
  name: string;
  icon: string;
  tagline: string;
  matchName: string;
  matchInitials: string;
  matchCity: string;
  matchRating: number;
  matchReviews: number;
  matchInterests: string[];
  inPersonCoins: number;
  virtualCoins: number;
  securityCode: string;
}

const OUTING_OPTIONS: OutingOption[] = [
  {
    id: 'dining',
    name: 'Fine Dining',
    icon: '🍷',
    tagline: 'Rooftop dinners, culinary tastings & engaging evening conversations',
    matchName: 'Ananya R.',
    matchInitials: 'AR',
    matchCity: 'Bangalore, Indiranagar',
    matchRating: 4.98,
    matchReviews: 34,
    matchInterests: ['Food & Wine', 'Tech & Startups', 'Modern Art'],
    inPersonCoins: 1200,
    virtualCoins: 40,
    securityCode: '8392',
  },
  {
    id: 'travel',
    name: 'Weekend Getaway',
    icon: '✈️',
    tagline: 'Scenic road trips, heritage walks & curated weekend adventures',
    matchName: 'Rohan M.',
    matchInitials: 'RM',
    matchCity: 'Mumbai & Goa',
    matchRating: 4.95,
    matchReviews: 29,
    matchInterests: ['Photography', 'Roadtrips', 'Architecture'],
    inPersonCoins: 1800,
    virtualCoins: 45,
    securityCode: '4917',
  },
  {
    id: 'events',
    name: 'Events & Galas',
    icon: '🎭',
    tagline: 'High-profile summits, cocktail galas, theatre & weddings',
    matchName: 'Meera K.',
    matchInitials: 'MK',
    matchCity: 'Delhi NCR',
    matchRating: 4.99,
    matchReviews: 42,
    matchInterests: ['High Fashion', 'Networking', 'Theatre'],
    inPersonCoins: 1500,
    virtualCoins: 50,
    securityCode: '6204',
  },
  {
    id: 'conversation',
    name: 'Deep Talks',
    icon: '☕',
    tagline: 'Heart-to-heart discussions, late-night venting & life perspectives',
    matchName: 'Kavya S.',
    matchInitials: 'KS',
    matchCity: 'Available Globally',
    matchRating: 4.97,
    matchReviews: 58,
    matchInterests: ['Psychology', 'Philosophy', 'Literature'],
    inPersonCoins: 900,
    virtualCoins: 35,
    securityCode: '1853',
  },
  {
    id: 'shopping',
    name: 'Shopping & Style',
    icon: '🛍️',
    tagline: 'Styling advice, boutique tours & relaxed cafe hopping',
    matchName: 'Vikram P.',
    matchInitials: 'VP',
    matchCity: 'Hyderabad, Jubilee Hills',
    matchRating: 4.94,
    matchReviews: 21,
    matchInterests: ['Luxury Style', 'Design', 'Specialty Coffee'],
    inPersonCoins: 1100,
    virtualCoins: 35,
    securityCode: '9041',
  },
];

export default function HeroExperienceSimulator() {
  const [selectedOuting, setSelectedOuting] = useState<OutingOption>(OUTING_OPTIONS[0]!);
  const [isVirtual, setIsVirtual] = useState<boolean>(false);
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [showMembershipModal, setShowMembershipModal] = useState<boolean>(false);

  const { session, profile } = useAuth();
  const { wallet } = useWallet();

  const isStaff = profile?.role === 'admin' || profile?.role === 'moderator';
  const hasMembership = wallet?.fees ? wallet.fees.userFeePaid || wallet.fees.userFeePaise === 0 || isStaff : isStaff;
  const userFeePaise = wallet?.fees?.userFeePaise ?? 19900;

  const handleCtaClick = () => {
    if (!session) {
      setShowAuthModal(true);
      return;
    }
    if (!hasMembership) {
      setShowMembershipModal(true);
      return;
    }
  };

  const rateDisplay = isVirtual
    ? `${selectedOuting.virtualCoins} coins / min`
    : `${selectedOuting.inPersonCoins} coins / hr`;

  return (
    <div className="relative mx-auto w-full min-w-0 max-w-full sm:max-w-lg">
      {/* Outer ambient glow */}
      <div className="absolute -inset-1 rounded-2xl sm:rounded-[36px] bg-gradient-to-r from-[var(--brandpink)] via-[var(--purple)] to-[var(--cyan)] opacity-70 blur-lg transition-all duration-500 sm:blur-xl hover:opacity-100" />

      {/* Main Glassmorphic Simulator Card */}
      <div className="relative w-full min-w-0 overflow-hidden rounded-2xl border border-white/20 bg-[#0B132B]/95 p-4 shadow-2xl backdrop-blur-2xl sm:rounded-[32px] sm:p-6 lg:p-7">
        {/* Header Bar */}
        <div className="flex items-center justify-between border-b border-white/10 pb-3 sm:pb-4">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5 sm:h-3 sm:w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--brand)] opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[var(--brand)] sm:h-3 sm:w-3" />
            </span>
            <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--cyan)] sm:text-xs">
              Outing Matcher
            </span>
          </div>
          <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] font-semibold text-slate-300 sm:px-2.5 sm:text-[11px]">
            Interactive Preview
          </span>
        </div>

        {/* Step 1: Occasion Selector Tabs */}
        <div className="mt-3 w-full min-w-0 sm:mt-4">
          <label className="text-[11px] font-semibold text-slate-300 sm:text-xs">1. Pick an occasion</label>
          <div className="mt-2 flex w-full min-w-0 max-w-full gap-1.5 overflow-x-auto pb-1.5 no-scrollbar [-webkit-overflow-scrolling:touch]">
            {OUTING_OPTIONS.map((opt) => {
              const active = selectedOuting.id === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setSelectedOuting(opt)}
                  className={`flex shrink-0 items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11px] font-semibold transition-all duration-200 sm:px-3 sm:text-xs ${
                    active
                      ? 'bg-gradient-to-r from-[var(--brand)] to-[var(--brandpink)] text-white shadow-md'
                      : 'border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <span>{opt.icon}</span>
                  <span>{opt.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 2: Format Toggle */}
        <div className="mt-3 flex w-full flex-col gap-1 rounded-xl border border-white/10 bg-black/40 p-1 xs:flex-row sm:mt-3.5 sm:rounded-2xl sm:p-1.5">
          <button
            type="button"
            onClick={() => setIsVirtual(false)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5 text-[11px] font-semibold transition-all sm:rounded-xl sm:text-xs ${
              !isVirtual
                ? 'bg-white/15 text-white shadow-sm ring-1 ring-white/20'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <span>📍 In-Person Meeting</span>
          </button>
          <button
            type="button"
            onClick={() => setIsVirtual(true)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5 text-[11px] font-semibold transition-all sm:rounded-xl sm:text-xs ${
              isVirtual
                ? 'bg-white/15 text-white shadow-sm ring-1 ring-white/20'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <span>💬 Online / Virtual Call</span>
          </button>
        </div>

        {/* Step 3: Dynamic Simulated Match Preview */}
        <div className="mt-3.5 w-full min-w-0 overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-white/10 to-white/5 p-3.5 text-white sm:mt-4 sm:rounded-2xl sm:p-4">
          <div className="flex flex-wrap items-start justify-between gap-2 sm:flex-nowrap">
            <div className="flex items-center gap-2.5 sm:gap-3">
              <div className="brand-ring grid h-10 w-10 shrink-0 place-items-center rounded-xl text-sm font-extrabold text-white shadow-md sm:h-12 sm:w-12 sm:rounded-2xl sm:text-base">
                {selectedOuting.matchInitials}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <h4 className="truncate text-sm font-bold text-white sm:text-base">{selectedOuting.matchName}</h4>
                  <span className="flex shrink-0 items-center gap-0.5 rounded-full bg-[var(--verified)]/20 px-1.5 py-0.5 text-[9px] font-bold text-[var(--verified)] sm:text-[10px]">
                    ✓ Verified
                  </span>
                </div>
                <p className="truncate text-[11px] text-slate-300 sm:text-xs">{selectedOuting.matchCity}</p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 sm:block sm:text-right">
              <div className="flex items-center justify-end gap-1 text-[11px] font-bold text-amber-300 sm:text-xs">
                <span>★</span>
                <span>{selectedOuting.matchRating}</span>
                <span className="text-[10px] font-normal text-slate-400">({selectedOuting.matchReviews})</span>
              </div>
              <p className="text-xs font-bold text-[var(--cyan)] sm:mt-0.5">{rateDisplay}</p>
            </div>
          </div>

          <p className="mt-2.5 w-full break-words text-[11px] leading-relaxed text-slate-200 sm:mt-3 sm:text-xs">
            {selectedOuting.tagline}
          </p>


          <div className="mt-2.5 flex flex-wrap gap-1 sm:mt-3">
            {selectedOuting.matchInterests.map((tag) => (
              <span
                key={tag}
                className="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[9px] font-medium text-slate-300 sm:text-[10px]"
              >
                #{tag}
              </span>
            ))}
          </div>

          {/* Safe PIN Security Demonstration */}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 bg-black/40 px-3 py-2 sm:mt-4">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 sm:text-[11px]">Safety PIN:</span>
              <div className="flex gap-1 sm:gap-1.5">
                {selectedOuting.securityCode.split('').map((digit, i) => (
                  <span
                    key={i}
                    className="grid h-5 w-4 place-items-center rounded border border-[var(--brand)]/60 bg-[var(--brand)]/20 text-[10px] font-extrabold text-white sm:h-6 sm:w-5 sm:rounded-md sm:text-xs"
                  >
                    {digit}
                  </span>
                ))}
              </div>
            </div>
            <span className="flex items-center gap-1 text-[10px] font-medium text-[var(--verified)] sm:text-[11px]">
              🛡️ Escrow Protected
            </span>
          </div>
        </div>

        {/* Action Button */}
        <div className="mt-4 sm:mt-5">
          {session && hasMembership ? (
            <Link
              to="/companions"
              className="btn-grad flex h-11 w-full items-center justify-center rounded-xl text-xs font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 sm:h-12 sm:text-sm"
            >
              Browse Matches for {selectedOuting.name} →
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleCtaClick}
              className="btn-grad flex h-11 w-full items-center justify-center rounded-xl text-xs font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 sm:h-12 sm:text-sm"
            >
              Find Partners for {selectedOuting.name} →
            </button>
          )}
        </div>
      </div>


      {/* Access modals */}
      <AuthPromptModal
        category={
          showAuthModal
            ? {
                id: 'simulator',
                slug: 'companions',
                name: selectedOuting.name,
                description: selectedOuting.tagline,
                guidance: '',
                iconKey: 'sparkles',
                hue: 330,
                allowedBookingTypes: isVirtual ? ['video', 'voice'] : ['in_person'],
              }
            : null
        }
        onClose={() => setShowAuthModal(false)}
      />

      <MembershipPromptModal
        category={
          showMembershipModal
            ? {
                id: 'simulator',
                slug: 'companions',
                name: selectedOuting.name,
                description: selectedOuting.tagline,
                guidance: '',
                iconKey: 'sparkles',
                hue: 330,
                allowedBookingTypes: isVirtual ? ['video', 'voice'] : ['in_person'],
              }
            : null
        }
        userFeePaise={userFeePaise}
        onClose={() => setShowMembershipModal(false)}
      />


    </div>
  );
}
